<template>
  <li
    v-if="link.type && link.type !== 'social'"
    class="navbar-first"
    :class="'is-' + link.type"
  >
    <!-- Support single links with no subtree/children -->
    <VuepalLink
      v-if="subtree.length === 0 && link.type !== 'search'"
      :to="link.url.path"
      class="navbar-first-link has-no-subtree"
    >
      {{ link.label }}
    </VuepalLink>
    <component
      v-else
      :is="linkComponent.is"
      v-bind="linkComponent"
      @mouseenter="onMouseEnter"
      @mouseleave="onMouseLeave"
      @click="onClick"
    >
      <template v-if="link.type === 'search'">
        <SpriteSymbol
          name="search"
          class="mr-2 mt-1 size-6 desktop:mr-0 desktop:size-4"
        />
        <span class="desktop:hidden">{{ $texts('search', 'Search') }}</span>
      </template>
      <span v-else>{{ link.label }}</span>
    </component>

    <StorageMenuOldNavigationNavbarFlyout
      v-if="subtree.length > 0 || link.type === 'search'"
      :title="link.label"
      :is-open="isOpen"
      :active-first-level="activeFirstLevel"
    >
      <template v-if="link.type === 'search' || $slots.content">
        <component
          :is="contentComponent"
          ref="content"
          :link="link"
          :subtree="subtree"
          :visible="isOpen"
        />
      </template>
      <component
        :is="contentComponent"
        v-else
        ref="content"
        :link="link"
        :subtree="subtree"
        :visible="isOpen"
        :active-first-level="activeFirstLevel"
      />
    </StorageMenuOldNavigationNavbarFlyout>
  </li>
</template>

<script lang="ts" setup>
import StorageMenuOldNavigationNavbarFirstLevelDefault from '../FirstLevel/Default/index.vue'
import StorageMenuOldNavigationNavbarFirstLevelSearch from '../FirstLevel/Search/index.vue'

const props = defineProps<{
  link: any
  subtree: any[]
  isOpen: boolean
  activeFirstLevel: number
  isMobileOpen: boolean
}>()

const { isUntilDesktop } = useViewport()

const renderComponent = ref(false)

const linkComponent = computed(() => {
  const component: Record<string, any> = {
    class: ['navbar-first-link'],
    name: props.link.label === '#' ? 'Social' : props.link.label,
  }

  if (props.isOpen) {
    component.class.push('is-open')
  }

  if (props.link.url.path !== '') {
    component.is = 'a'
    component.href = props.link.url.path
  } else {
    component.is = 'button'
    component.class.push('button-reset')
    component['aria-label'] = props.link.label
  }

  return component
})

const contentComponent = computed(() => {
  if (props.link.type === 'search')
    return StorageMenuOldNavigationNavbarFirstLevelSearch
  return StorageMenuOldNavigationNavbarFirstLevelDefault
})

function onMouseEnter() {
  if (isUntilDesktop.value) {
    return
  }
  renderComponent.value = true
}

function onMouseLeave() {
  if (isUntilDesktop.value) {
    return
  }
}

function onClick(e: Event) {
  if (isUntilDesktop.value || !props.isMobileOpen) {
    e.preventDefault()
    renderComponent.value = true
  }
}
</script>

<style lang="postcss">
.navbar-first-link {
  @apply relative block w-full cursor-pointer px-[1rem] py-0 pb-[1rem] text-xl leading-none text-muted;
  &:hover {
    @apply text-scheme-invert-bis;
  }

  @apply belowDesktop:flex belowDesktop:text-left belowDesktop:text-size-4;
  @screen belowDesktop {
    padding: 0.5rem 1rem;
    > .icon {
      font-size: 1em;
      margin-bottom: -0.2em;
      margin-top: -0.2em;
      transform: translateY(0.15em);
      margin-right: 0.1em;
    }
    span {
      transition: 0.5s;
    }

    &.is-open {
      span {
        opacity: 0;
      }
      &:after {
        transform: translate(calc((100vw * -1) + 3rem), -50%) rotate(180deg);
        color: white;
      }
    }
  }

  @apply desktop:w-auto desktop:px-0;
  @screen desktop {
    margin-left: 1.5rem;
    > .icon {
      font-size: 0.9em;
      margin-top: -0.2em;
      transform: translateY(0.15em);
    }
    &.is-open {
      &:after {
        transform: none;
      }
    }
  }

  &.is-open {
    @apply desktop:text-scheme-invert;
  }

  &:after {
    @apply belowDesktop:pointer-events-none belowDesktop:absolute;
    @screen belowDesktop {
      content: '➡';
      top: 50%;
      right: 1rem;
      transform: translateY(-50%);
      z-index: 140;
      transition: 0.5s;
    }
    @apply desktop:absolute desktop:left-0 desktop:block desktop:w-full desktop:bg-scheme-invert;

    @screen desktop {
      content: '';
      bottom: -1px;
      transform-origin: center bottom;
      transform: scaleY(0);
      transition: 0.5s;
      z-index: 10;
      height: 3px;
    }
  }

  &.has-no-subtree {
    &:after {
      @apply hidden;
    }
  }
}

.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: 0.5s;
}
.overlay-fade-enter,
.overlay-fade-leave-to {
  opacity: 0;
}
</style>
